/**
 * Pick the active Australian financial-year rate block from financial-rates.json.
 * Schedules use effectiveFrom (inclusive) and optional effectiveTo (inclusive end-of-day UTC).
 */

const FY_START_MONTH = 6; // July (0-indexed)

/** @returns {string} e.g. "2026-27" for a date in that Australian FY */
export function australianFinancialYearLabel(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const month = d.getUTCMonth();
  const year = d.getUTCFullYear();
  const startYear = month >= FY_START_MONTH ? year : year - 1;
  const endYear = (startYear + 1) % 100;
  return `${startYear}-${String(endYear).padStart(2, '0')}`;
}

function parseScheduleDate(value) {
  if (!value) return null;
  return new Date(`${value}T00:00:00.000Z`);
}

function scheduleIsActive(schedule, date) {
  const from = parseScheduleDate(schedule.effectiveFrom);
  if (!from || date < from) return false;
  if (schedule.effectiveTo) {
    const to = parseScheduleDate(schedule.effectiveTo);
    if (to) {
      const end = new Date(to);
      end.setUTCHours(23, 59, 59, 999);
      if (date > end) return false;
    }
  }
  return true;
}

/**
 * @param {object} json - financial-rates.json root
 * @param {Date|string} [asAt] - defaults to now (UTC calendar day)
 * @returns {object} flat rates object for hydrate / loader (no `schedules` key)
 */
export function resolveFinancialYearRates(json, asAt = new Date()) {
  if (!json || typeof json !== 'object') return json;

  const date = asAt instanceof Date ? asAt : new Date(asAt);
  const { schedules, ...shared } = json;

  if (!Array.isArray(schedules) || schedules.length === 0) {
    return json;
  }

  let active = schedules.find((s) => scheduleIsActive(s, date));
  if (!active) {
    const sorted = [...schedules].sort(
      (a, b) =>
        (parseScheduleDate(a.effectiveFrom)?.getTime() ?? 0) -
        (parseScheduleDate(b.effectiveFrom)?.getTime() ?? 0)
    );
    const earliestFrom = parseScheduleDate(sorted[0].effectiveFrom);
    active = earliestFrom && date < earliestFrom ? sorted[0] : sorted[sorted.length - 1];
  }

  const { effectiveFrom, effectiveTo, financialYear, ...scheduleRates } = active;

  return {
    ...shared,
    ...scheduleRates,
    // Deep-merge so FY flags (e.g. division296.enabled) do not drop shared method/notes fields
    division296: {
      ...(shared.division296 || {}),
      ...(scheduleRates.division296 || {}),
    },
    financialYear: financialYear || scheduleRates.version,
    metadata: {
      ...(shared.metadata || {}),
      ...(scheduleRates.metadata || {}),
      financialYear: financialYear || scheduleRates.version,
      resolvedAt: date.toISOString(),
    },
  };
}

export default resolveFinancialYearRates;
